/**
 * Curated short list of country / region options shown across post-generation
 * UIs (Researched Posts, POV Posts, Prompt Posts).
 *
 * We keep this deliberately small (~20 major markets) so the dropdown stays
 * a quick, glanceable picker rather than a 195-row scroll. "Global" is the
 * fallback when the user does NOT want any locale-specific grounding.
 *
 * The string value here is what we send to the backend and what the writer
 * model receives. Keep the labels canonical (no abbreviations) — the LLM
 * uses these directly when picking local examples / currency / spelling.
 */
export const POST_COUNTRY_OPTIONS = [
  { value: 'Global', label: 'Global (no locale anchor)' },
  { value: 'India', label: 'India' },
  { value: 'United States', label: 'United States' },
  { value: 'United Kingdom', label: 'United Kingdom' },
  { value: 'Canada', label: 'Canada' },
  { value: 'Australia', label: 'Australia' },
  { value: 'Singapore', label: 'Singapore' },
  { value: 'United Arab Emirates', label: 'United Arab Emirates' },
  { value: 'Saudi Arabia', label: 'Saudi Arabia' },
  { value: 'Germany', label: 'Germany' },
  { value: 'France', label: 'France' },
  { value: 'Netherlands', label: 'Netherlands' },
  { value: 'Ireland', label: 'Ireland' },
  { value: 'Hong Kong', label: 'Hong Kong' },
  { value: 'Japan', label: 'Japan' },
  { value: 'South Korea', label: 'South Korea' },
  { value: 'Brazil', label: 'Brazil' },
  { value: 'Mexico', label: 'Mexico' },
  { value: 'South Africa', label: 'South Africa' },
  { value: 'Indonesia', label: 'Indonesia' },
  { value: 'Philippines', label: 'Philippines' },
];

/**
 * Resolve the best default country option for a post-generation dialog.
 *
 * Order of preference:
 *   1. Explicit override passed in (e.g. last-saved selection).
 *   2. The auto-inferred geography on the analysis' post_context.
 *   3. "Global" — the safe no-op anchor.
 *
 * We loosely match against the curated list (case-insensitive). If the
 * incoming value isn't on the list (e.g. "Bengaluru, India" or "UK"), we
 * try to map it to a known entry; otherwise we fall back to "Global" so
 * the Select component never renders an unknown value.
 */
export function resolveDefaultCountry(...candidates) {
  const tryMatch = (raw) => {
    if (!raw || typeof raw !== 'string') return null;
    const v = raw.trim();
    if (!v) return null;
    // Exact match first
    const exact = POST_COUNTRY_OPTIONS.find(
      (c) => c.value.toLowerCase() === v.toLowerCase(),
    );
    if (exact) return exact.value;
    // Loose contains-match (e.g. "Bengaluru, India" -> India)
    const lower = v.toLowerCase();
    const partial = POST_COUNTRY_OPTIONS.find(
      (c) => c.value !== 'Global' && lower.includes(c.value.toLowerCase()),
    );
    if (partial) return partial.value;
    // Common short codes the model sometimes emits
    const codeMap = {
      uk: 'United Kingdom',
      us: 'United States',
      usa: 'United States',
      uae: 'United Arab Emirates',
      sg: 'Singapore',
      ind: 'India',
    };
    if (codeMap[lower]) return codeMap[lower];
    return null;
  };
  for (const c of candidates) {
    const matched = tryMatch(c);
    if (matched) return matched;
  }
  return 'Global';
}
